import { Link } from 'react-router-dom';
import { shopifyImg, type ShopifyProduct } from '@/lib/shopify';
import { dlSelectItem, shopifyNodeToItem } from '@/lib/dataLayer';
import { cn } from '@/lib/utils';
import trio from '@/assets/home-v2/kit-trio.webp.asset.json';
import quarteto from '@/assets/home-v2/kit-quarteto.webp.asset.json';
import linha5 from '@/assets/home-v2/hero-linha5.webp.asset.json';
import { useProductsByHandle, useViewItemList, priceInfo, Reveal, Eyebrow, H2 } from './shared';
import { PurchaseBenefits } from './PurchaseBenefits';

const KITS = [
  { handle: 'kit-regeneracao-e-hidratacao-intensiva', img: linha5.url as string | null, big: true },
  { handle: 'kit-hidratacao-e-limpeza-suave', img: quarteto.url, big: false },
  { handle: 'kit-hidratacao-diaria-essencial', img: trio.url, big: false },
];

const KitCard = ({ product, img, big }: { product?: ShopifyProduct; img: string | null; big: boolean }) => {
  const info = priceInfo(product);
  const shopImg = product?.node.images.edges[0]?.node;
  const src = img ?? (shopImg ? shopifyImg(shopImg.url, 900) : undefined);
  if (!product) return <div className={cn('rounded-3xl bg-v2-ink/5 animate-pulse min-h-[280px]', big && 'md:min-h-[400px]')} />;
  return (
    <Link
      to={`/produto/${product.node.handle}`}
      onClick={() => { try { dlSelectItem(shopifyNodeToItem(product.node, info?.current ?? 0, 1)); } catch {} }}
      className={cn(
        'group relative rounded-3xl bg-background border border-v2-ink/10 overflow-hidden flex flex-col',
        big && 'md:grid md:grid-cols-[3fr_2fr]',
      )}
    >
      {info && info.discountPct > 0 && (
        <span className="absolute top-3 right-3 z-10 text-sm font-semibold px-2.5 py-1 rounded-full bg-v2-ink text-v2-bg">−{info.discountPct}%</span>
      )}
      <div className={cn('bg-v2-bg overflow-hidden', big ? 'aspect-[3/2]' : 'aspect-[4/3]')}>
        {src && <img src={src} alt={product.node.title} width={big ? 1200 : 900} height={big ? 800 : 675} loading="lazy" decoding="async" className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105" />}
      </div>
      <div className={cn('p-5', big && 'md:p-8 md:flex md:flex-col md:justify-center')}>
        <h3 className="font-narrow text-xl font-semibold leading-tight">{product.node.title}</h3>
        {info && (
          <div className="mt-2 flex items-baseline gap-2 flex-wrap">
            {info.compare && <span className="text-sm line-through text-v2-muted">{info.compare}</span>}
            <span className="text-lg font-semibold">{info.price}</span>
            {info.savings && <span className="w-full text-sm font-semibold text-v2-bal">Você economiza {info.savings}</span>}
          </div>
        )}
        {big && (
          <span className="mt-5 inline-flex min-h-12 items-center justify-center rounded-full bg-v2-ink px-6 font-semibold text-v2-bg">
            Ver kit
          </span>
        )}
        {big && <PurchaseBenefits />}
      </div>
    </Link>
  );
};

export const KitsBento = () => {
  const { map } = useProductsByHandle();
  const products = KITS.map((k) => map.get(k.handle));
  useViewItemList('Kits home', products);

  return (
    <Reveal as="section" className="bg-v2-bg text-v2-ink py-16 md:py-24">
      <div className="container mx-auto px-5 max-w-6xl">
        <Eyebrow>Rotinas prontas</Eyebrow>
        <H2>Monte menos. Cuide mais.</H2>
        <div className="mt-10 space-y-4">
          <KitCard product={products[0]} img={KITS[0].img} big />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {KITS.slice(1).map((k, i) => (
              <KitCard key={k.handle} product={products[i + 1]} img={k.img} big={false} />
            ))}
          </div>
        </div>
      </div>
    </Reveal>
  );
};
